/**
 * Auth Context — Manages auth state for the app (dual: cookie session + JWT)
 *
 * Provides:
 * - useAuth: hook to access auth state + login/logout
 * - getStoredToken: read the persisted JWT (for api.ts outside the React tree)
 * - The shared AuthContext + persisted-state helpers the provider builds on
 *
 * AuthProvider itself lives in auth-provider.tsx so this file only exports
 * non-components (react-refresh/only-export-components).
 */

import { createContext, useContext } from 'react';

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  platformRole?: string | null;
}

export interface AuthOrgEntitlements {
  scout: boolean;
  scheduler: boolean;
}

export interface AuthOrg {
  orgId: string;
  name: string;
  role: string;
  /** Absent on stale JWT-era localStorage entries; always present in /auth/login and /auth/me responses. */
  entitlements?: AuthOrgEntitlements;
}

export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  org: AuthOrg | null;
  isAuthenticated: boolean;
}

export interface AuthContextValue extends AuthState {
  /**
   * True once auth state reflects the server (login, /auth/me revalidation,
   * or no cookie session to revalidate). Until then, state restored from
   * localStorage is only a paint-fast hint and may be stale.
   */
  authResolved: boolean;
  /** Cookie-only login: token may be null. Pass user + org returned by /auth/login or /auth/me. */
  login: (token: string | null, user: AuthUser, org: AuthOrg) => void;
  logout: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
  /**
   * Re-fetch /auth/me and overwrite user/org with the server response. Needed
   * after flows whose response org omits entitlements (accept-invite) — the
   * mount-time revalidation only runs once, so entitlement-gated UI would
   * otherwise stay wrong until a full reload.
   */
  revalidateAuth: () => Promise<void>;
}

export const AUTH_STORAGE_KEY = '11to1_auth';

export function loadPersistedAuth(): AuthState {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.token && parsed.user && parsed.org) {
        return {
          token: parsed.token,
          user: parsed.user,
          org: parsed.org,
          isAuthenticated: true,
        };
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { token: null, user: null, org: null, isAuthenticated: false };
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/**
 * Get the current auth token (for use in api.ts outside React tree)
 */
export function getStoredToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.token || null;
    }
  } catch {
    // Ignore
  }
  return null;
}
